/**
 * Reading tables Word may refuse to report.
 *
 * `table.values` fails with a bare GeneralException on a table that is a
 * tracked deletion (every row marked deleted), and one failing table fails
 * the whole batch it was loaded in. The push sheet read every table of every
 * answer in one batch, so one table io.ai had deleted as a tracked change
 * took the whole read down ("Send 0 questions", PE-604 testing, 1 Oct).
 * Tables are therefore read together first and one by one when that fails,
 * and a table Word cannot report is described by its reviewed text: nothing
 * left means it is deleted.
 */
import { describeError } from "./errors";

export type TableRead = {
  /** Cell values, or null when Word could not report them. */
  values: string[][] | null;
  headerRowCount: number;
  /** The table is a tracked deletion: nothing remains once changes are accepted. */
  deleted: boolean;
  /** What remains of an unreportable table, as text. */
  text: string;
};

const readOne = async (context: Word.RequestContext, table: Word.Table): Promise<TableRead> => {
  try {
    table.load("values,headerRowCount");
    await context.sync();
    return { values: table.values ?? [], headerRowCount: table.headerRowCount ?? 0, deleted: false, text: "" };
  } catch (error) {
    console.warn("Word could not report a table; reading its reviewed text instead", describeError(error));
  }
  let text = "";
  try {
    const current = table.getRange("Whole").getReviewedText("Current");
    await context.sync();
    text = (current.value ?? "").trim();
  } catch {
    // No reviewed text either: treat it as gone.
  }
  return { values: null, headerRowCount: 0, deleted: !text, text };
};

/**
 * Every table in the collection, in order, tolerating ones Word can't report.
 * @param context - The batch.
 * @param tables - The tables to read; loaded here.
 * @returns One read per table, aligned with the collection's order.
 */
export const readTables = async (context: Word.RequestContext, tables: Word.TableCollection): Promise<TableRead[]> => {
  tables.load("items");
  await context.sync();
  const items = tables.items;
  if (!items.length) return [];
  try {
    for (const t of items) t.load("values,headerRowCount");
    await context.sync();
    return items.map((t) => ({
      values: t.values ?? [],
      headerRowCount: t.headerRowCount ?? 0,
      deleted: false,
      text: "",
    }));
  } catch (error) {
    console.warn("Could not read every table at once; reading them one by one", describeError(error));
  }
  const out: TableRead[] = [];
  for (const t of items) out.push(await readOne(context, t));
  return out;
};
